import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { logger } from '@/lib/logger';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send token from localStorage as fallback
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // First try with credentials (cookies)
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      // If cookie auth fails, try with localStorage token (fallback for mobile/Android WebView)
      const token = localStorage.getItem('session_token');
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (tokenError) {
          // Both methods failed, clear token and redirect to login
          logger.error('Auth check failed with token:', tokenError);
          localStorage.removeItem('session_token');
          setUser(null);
          // Store message to show on login page
          sessionStorage.setItem('auth_message', 'Your session has expired. Please log in again.');
        }
      } else {
        // No cookie and no localStorage token - user needs to log in
        logger.log('No authentication found - user needs to log in');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Store token in localStorage as fallback for mobile
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      setUser(response.data);
      return response.data;
    } catch (error) {
      logger.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email, name, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        { email, name, password },
        { withCredentials: true }
      );
      // Store token in localStorage as fallback for mobile
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      setUser(response.data);
      return response.data;
    } catch (error) {
      logger.error('Registration error:', error);
      throw error;
    }
  };

  const googleAuth = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/google`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    // Store token in localStorage as fallback for mobile
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      localStorage.removeItem('session_token');
      sessionStorage.removeItem('login_redirect');
      setUser(null);
    }
  };

  const updateLanguage = async (language) => {
    await axios.put(
      `${API}/auth/language`,
      { language },
      { withCredentials: true }
    );
    setUser({ ...user, language });
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, googleAuth, logout, updateLanguage }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};